import { BASE_URL } from "../config";

export async function fetchContacts(token, page = 1, pageSize = 10, search = "", category = "", emailSentFilter = "", leadStatusFilter = "") {
  let url = `${BASE_URL}api/contacts/list?page=${page}&size=${pageSize}`;
  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }
  if (category) {
    url += `&category=${encodeURIComponent(category)}`;
  }
  if (emailSentFilter) {
    url += `&email_sent=${encodeURIComponent(emailSentFilter)}`;
  }
  if (leadStatusFilter) {
    url += `&lead_status=${encodeURIComponent(leadStatusFilter)}`;
  }

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) throw new Error("Failed to fetch contacts");
  return await response.json(); // { count, results, current_page, total_pages }
}

export async function createContactFromRawText(token, rawText) {
  const response = await fetch(`${BASE_URL}api/contacts/import-contacts`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ raw_text: rawText }),
  });

  if (!response.ok) throw new Error("Failed to create contact from raw text");
  return await response.json(); // { id }
}

export async function createBulkContactFromJson(token, json) {
  const response = await fetch(`${BASE_URL}api/contacts/create`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(json),
  });

  if (!response.ok) throw new Error("Failed to create bulk contact from JSON");
  return await response.json(); // { id }
}


export async function fetchCategoryStats(token) {
  const response = await fetch(`${BASE_URL}api/contacts/category-stats`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) throw new Error("Failed to fetch category stats");
  return await response.json(); // { categories: [{ name, count }] }
}

export async function mergeCategories(token, source_category_1, source_category_2, new_category) {
  const response = await fetch(`${BASE_URL}api/contacts/merge-categories`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ source_category_1, source_category_2, new_category }),
  });

  if (!response.ok) throw new Error("Failed to merge categories");
}

export async function fetchContactDetails(token, contactId) {
  const response = await fetch(`${BASE_URL}api/contacts/update/${contactId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) throw new Error("Failed to fetch contact details");
  return await response.json(); // { id, first_name, last_name, email, phone, category, created_at, updated_at }
}

export async function fetchEmailTemplatesList(token) {
  const response = await fetch(`${BASE_URL}api/contacts/email-templates`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) throw new Error("Failed to fetch email templates list");
  return await response.json();
}

export async function fetchEmailTemplate(token, id) {
  const response = await fetch(`${BASE_URL}api/contacts/update-template/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to fetch email template");
  }
  return await response.json();
}

export async function createEmailTemplate(token, name, subject, body) {
  const response = await fetch(`${BASE_URL}api/contacts/create-template`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name, subject, body }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to create email template");
  }
  return await response.json(); // { id }
}

export async function updateEmailTemplate(token, id, templateData) {
  const response = await fetch(`${BASE_URL}api/contacts/update-template/${id}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(templateData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to update email template");
  }
  return await response.json();
}

export async function deleteEmailTemplate(token, id) {
  const response = await fetch(`${BASE_URL}api/contacts/update-template/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to delete email template");
  }
  return true;
}

export async function sendQuickEmail(token, emails, template_id, track_opens = false, include_unsubscribe = false) {
  const response = await fetch(`${BASE_URL}api/contacts/quick-email-send`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ emails, template_id, track_opens, include_unsubscribe }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to send quick email");
  }
  return await response.json();
}

export async function sendEmail(token, category, template_id, track_opens = false, include_unsubscribe = false) {
  const response = await fetch(`${BASE_URL}api/contacts/email-send`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ category, template_id, track_opens, include_unsubscribe }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to send email");
  }
  return await response.json();
}

export async function fetchEmailLogs(token, page = 1, pageSize = 10) {
  const response = await fetch(`${BASE_URL}api/contacts/email-logs?page=${page}&size=${pageSize}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) throw new Error("Failed to fetch email logs");
  return await response.json(); // { count, results, current_page, total_pages }
}


export async function importContactsFromDubaiBusinessDirectory(token){
  const response = await fetch(`${BASE_URL}api/contacts/import-dubai-business-directory`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) throw new Error("Failed to import contacts from Dubai Business Directory");
  return await response.json();
}

export async function fetchEmailStats(token) {
  const response = await fetch(`${BASE_URL}api/contacts/email-stats`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) throw new Error("Failed to fetch email logs");
  return await response.json(); // { count, results, current_page, total_pages }
}

export async function unsentContactCount(token) {
  const response = await fetch(`${BASE_URL}api/contacts/unsent-contact-count`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) throw new Error("Failed to fetch unsent contact count");
  return await response.json();
}

export async function sendTestEmail(token, test_email, template_id, subject = "", body = "") {
  const response = await fetch(`${BASE_URL}api/contacts/send-test-email`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ test_email, template_id, subject, body }),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message || "Failed to send test email");
  return data;
}

export async function updateContactStatus(token, contactId, lead_status) {
  const response = await fetch(`${BASE_URL}api/contacts/update/${contactId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ lead_status }),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message || "Failed to update lead status");
  return data;
}

export async function fetchFollowUpSequence(token) {
  const response = await fetch(`${BASE_URL}api/contacts/followup-sequences`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) throw new Error("Failed to fetch follow-up sequence");
  return await response.json();
}

export async function saveFollowUpSequence(token, is_active, steps) {
  const response = await fetch(`${BASE_URL}api/contacts/followup-sequences`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ is_active, steps }),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message || "Failed to save follow-up sequence");
  return data;
}

export async function sendSelectedContactsEmail(token, contact_ids, template_id, track_opens = false, include_unsubscribe = false) {
  const response = await fetch(`${BASE_URL}api/contacts/send-selected-email`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ contact_ids, template_id, track_opens, include_unsubscribe }),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message || "Failed to send email to selected contacts");
  return data;
}





